import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import {
  channelLabel,
  getConnectivity,
  type ConnectivityReport,
} from "../api/connectivity";

/** Global banner: shows at the top of every page when any broker connectivity
 *  channel (login/REST, order placement, or the live price feed) is failing,
 *  naming exactly what's broken. Silent when everything is healthy or when no
 *  check has run yet. Polls every 30s so a channel dropping mid-session (or the
 *  5-min backend sweep flagging it) appears without a manual refresh. */
export default function ConnectivityBanner() {
  const { data } = useQuery({
    queryKey: ["connectivity"],
    queryFn: getConnectivity,
    refetchInterval: 30_000,
    // Don't spam errors into the UI if the endpoint itself is unreachable.
    retry: false,
  });

  const reports: ConnectivityReport[] = data ?? [];
  const broken = reports.filter((r) => !r.ok);
  if (broken.length === 0) return null;

  return (
    <div className="border-b border-down-300 dark:border-down-800 bg-down-50 dark:bg-down-950/40">
      <div className="max-w-7xl mx-auto px-4 py-2.5 sm:px-6 lg:px-8 space-y-1.5">
        {broken.map((r) => (
          <div key={r.connection_id} className="flex items-start gap-2 text-sm">
            <AlertTriangle
              size={16}
              className="mt-0.5 shrink-0 text-down-600 dark:text-down-400"
            />
            <div className="min-w-0 break-words">
              <span className="font-semibold text-down-700 dark:text-down-300">
                {r.broker} ({r.label}):
              </span>{" "}
              {r.channels
                .filter((c) => !c.ok)
                .map((c, i) => (
                  <span key={c.channel}>
                    {i > 0 && " · "}
                    <span className="font-medium">{channelLabel(c.channel)}</span>
                    {" — "}
                    <span className="text-fg/80">{c.message}</span>
                  </span>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
